import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, Send, Printer, Pen, ShieldCheck } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
} from '@roger/ui-components';
import {
  GenericOrderForm,
  type GenericOrderFormHandle,
} from './GenericOrderForm';
import { CMS485OrderForm } from './CMS485OrderForm';
import TaskDocumentsSection from '../documents/TaskDocumentsSection';
import { AutoSaveComments } from '../intake/shared';

import { Loader2 } from 'lucide-react';
import {
  signTask,
  approveTask,
  breakTaskSignatures,
  getOrder,
  getTaskSignatures,
  getAvailableClinicians,
  updateEpisodeAssignments,
  updateTaskNotes,
  client,
} from '../../services/api';
import {
  getTaskDocumentsTasksTaskIdDocumentsGet,
  getTaskDocumentUrlTasksTaskIdDocumentsDocumentIdUrlGet,
} from '../../services/api/sdk.gen';
import type {
  OrderResponse,
  TaskSignatureResponse,
  AvailableClinicianResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import {
  EDITABLE_STATUSES,
  STATUS_DISPLAY_LABELS,
  getStatusBadgeVariant,
  formatDate,
  formatClinicianName,
  useTaskPermissions,
} from '../task/TaskDetailPanel';
import { AttestationDialog } from '../signing/AttestationDialog';
import { SignatureCard } from '../signing/SignatureCard';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';

interface OrderDetailPanelProps {
  orderId: string;
  onBack?: () => void;
  onVisitUpdated?: (visit: OrderResponse) => void;
}

export const OrderDetailPanel: React.FC<OrderDetailPanelProps> = ({
  orderId,
  onBack,
  onVisitUpdated,
}) => {
  const user = useStore(state => state.user);
  const { error: showError } = useToast();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  const [visit, setVisit] = useState<OrderResponse | null>(null);
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const [signingLoading, setSigningLoading] = useState(false);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [physicians, setPhysicians] = useState<AvailableClinicianResponse[]>(
    []
  );
  const [caseManagers, setCaseManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [cms485PdfUrl, setCms485PdfUrl] = useState<string | null>(null);
  const [cms485PdfLoading, setCms485PdfLoading] = useState(false);
  const genericFormRef = useRef<GenericOrderFormHandle>(null);

  // --- Data fetching ---

  const fetchData = async () => {
    try {
      setLoading(true);

      const [visitData, signaturesData] = await Promise.all([
        getOrder({ path: { order_id: orderId }, client }),
        getTaskSignatures({ path: { task_id: orderId }, client }),
      ]);

      if (visitData?.data) {
        setVisit(visitData.data as unknown as OrderResponse);
        onVisitUpdated?.(visitData.data as unknown as OrderResponse);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error fetching order data:', err);
      showError('Load Failed', 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const isReadonly = !EDITABLE_STATUSES.has(visit?.status ?? 'not_started');

  useEffect(() => {
    fetchData();
  }, [orderId]);

  useEffect(() => {
    const fetchClinicians = async () => {
      try {
        const res = await getAvailableClinicians({
          client,
          query: visit?.agency_clinic_id
            ? { clinic_id: visit.agency_clinic_id }
            : {},
        });
        if (res.data) {
          setPhysicians(res.data.filter(c => c.is_physician));
          setCaseManagers(res.data.filter(c => c.is_case_manager));
        }
      } catch (err) {
        console.error('Failed to fetch clinicians:', err);
      }
    };
    if (!loading) fetchClinicians();
  }, [loading, visit?.agency_clinic_id]);

  // Load the signed CMS-485 PDF (persisted to the task at sign time) for readonly mode
  useEffect(() => {
    if (visit?.order_type !== 'CMS_485' || !isReadonly || loading) return;

    let cancelled = false;
    const loadPdf = async () => {
      setCms485PdfLoading(true);
      try {
        const docsRes = await getTaskDocumentsTasksTaskIdDocumentsGet({
          client,
          path: { task_id: orderId },
        });
        if (docsRes.error) throw new Error('Failed to load task documents');
        const cms485Doc = (docsRes.data ?? []).find(
          d => d.document_type === 'cms_485'
        );
        if (!cms485Doc) {
          if (!cancelled) setCms485PdfUrl(null);
          return;
        }
        const urlRes =
          await getTaskDocumentUrlTasksTaskIdDocumentsDocumentIdUrlGet({
            client,
            path: { task_id: orderId, document_id: cms485Doc.id },
          });
        if (urlRes.error) throw new Error('Failed to load CMS-485 URL');
        if (!cancelled) setCms485PdfUrl(urlRes.data?.url ?? null);
      } catch (err) {
        console.error('Failed to load CMS-485 PDF:', err);
        if (!cancelled) setCms485PdfUrl(null);
      } finally {
        if (!cancelled) setCms485PdfLoading(false);
      }
    };
    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [visit?.order_type, isReadonly, loading, orderId]);

  const handleAssignmentChange = async (
    field: 'assigned_physician_id' | 'case_manager_id',
    value: string
  ) => {
    if (!visit) return;
    try {
      await updateEpisodeAssignments({
        path: { episode_id: visit.episode_id },
        body: { [field]: value },
        client,
      });
      await fetchData();
    } catch (err) {
      console.error('Failed to update assignment:', err);
      showError('Update Failed', 'Failed to update assignment.');
    }
  };

  // --- Derived permission state ---

  const { hasCaseManagerRole, canSign, canApprove, canBreakSignatures } =
    useTaskPermissions(user, visit);

  const hasRequiredAssignments =
    !!visit?.episode_physician_id && !!visit?.episode_case_manager_id;

  // Status gate removed per ROG-1942 — comments enabled for case managers at all
  // order statuses. Backend enforces role authorization via PUT /tasks/{id}/notes.
  const canEditNotes = hasCaseManagerRole;

  // --- Refresh helpers ---

  const refreshVisitAndSignatures = async () => {
    const [visitData, signaturesData] = await Promise.all([
      getOrder({ path: { order_id: orderId }, client }),
      getTaskSignatures({ path: { task_id: orderId }, client }),
    ]);
    if (visitData?.data) {
      setVisit(visitData.data as unknown as OrderResponse);
      onVisitUpdated?.(visitData.data as unknown as OrderResponse);
    }
    if (signaturesData?.data) {
      setSignatures(signaturesData.data);
    }
  };

  // --- Action handlers ---

  const orderAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('order', visit?.order_type ?? null)
    );
  }, [attestations, visit?.order_type]);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);

  const handleSignAndSubmit = async () => {
    if (visit?.order_type !== 'CMS_485' && genericFormRef.current) {
      // Flush any pending debounced auto-save so the backend sees the latest
      // field values before the sign call. If the flush fails, abort the sign.
      try {
        await genericFormRef.current.flush();
      } catch (err) {
        console.error('Failed to flush pending save before signing:', err);
        showError(
          'Save Failed',
          'Could not save pending changes. Please retry.'
        );
        return;
      }
      const isValid = await genericFormRef.current.validate();
      if (!isValid) return;
    }
    setSignAttestationOpen(true);
  };

  const doSignAndSubmit = async () => {
    if (!orderAttestation) return;

    try {
      setSigningLoading(true);
      const result = await signTask({
        path: { task_id: orderId },
        body: { attestation_version: orderAttestation.version },
        client,
      });
      if (result.error) {
        console.error('Error signing order:', result.error);
        showError(
          'Sign Failed',
          'Failed to sign and submit. Please try again.'
        );
        return;
      }
      await refreshVisitAndSignatures();
    } catch (err) {
      console.error('Error signing order:', err);
      showError('Sign Failed', 'Failed to sign and submit. Please try again.');
    } finally {
      setSigningLoading(false);
      setSignAttestationOpen(false);
    }
  };

  const handleApprove = async () => {
    setApproveAttestationOpen(true);
  };

  const doApprove = async () => {
    if (!orderAttestation) return;

    try {
      setApprovingLoading(true);
      const result = await approveTask({
        path: { task_id: orderId },
        body: { attestation_version: orderAttestation.version },
        client,
      });
      if (result.error) {
        console.error('Error approving order:', result.error);
        showError(
          'Approve Failed',
          'Failed to complete order. Please try again.'
        );
        return;
      }
      await refreshVisitAndSignatures();
    } catch (err) {
      console.error('Error approving order:', err);
      showError(
        'Approve Failed',
        'Failed to complete order. Please try again.'
      );
    } finally {
      setApprovingLoading(false);
      setApproveAttestationOpen(false);
    }
  };

  const handleBreakSignatures = async () => {
    try {
      setEditingLoading(true);
      const result = await breakTaskSignatures({
        path: { task_id: orderId },
        client,
      });
      if (result.error) {
        console.error('Error unlocking order:', result.error);
        showError('Unlock Failed', 'Failed to unlock order. Please try again.');
        return;
      }
      await refreshVisitAndSignatures();
    } catch (err) {
      console.error('Error unlocking order:', err);
      showError('Unlock Failed', 'Failed to unlock order. Please try again.');
    } finally {
      setEditingLoading(false);
    }
  };

  // --- Render ---

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-muted-foreground'>Order not found.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='flex items-center justify-between px-5 py-3 border-b bg-muted/20'>
        <div className='flex items-center gap-3 min-w-0'>
          {onBack && (
            <Button
              variant='outline'
              size='sm'
              onClick={onBack}
              className='flex items-center gap-1.5 flex-shrink-0'
            >
              <ArrowLeft className='h-3.5 w-3.5' />
              Back
            </Button>
          )}
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium text-foreground'>
                {visit.order_type_display_name}
              </span>
              <span className='text-sm text-muted-foreground'>
                {formatDate(visit.order_date)}
              </span>
            </div>
          </div>
        </div>

        <div className='flex items-center gap-2 flex-shrink-0'>
          {/* Unlock & Edit */}
          {canBreakSignatures && (
            <Button
              onClick={handleBreakSignatures}
              disabled={editingLoading}
              size='sm'
              variant='outline'
              className='flex items-center gap-1.5'
            >
              {editingLoading ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                  Processing...
                </>
              ) : (
                <>
                  <Pen className='h-4 w-4' />
                  Unlock & Edit
                </>
              )}
            </Button>
          )}

          {/* Sign & Submit */}
          {canSign && (
            <Button
              onClick={handleSignAndSubmit}
              disabled={
                signingLoading ||
                !hasRequiredAssignments ||
                attestationsLoading ||
                !orderAttestation
              }
              title={
                !hasRequiredAssignments
                  ? 'Assign physician and case manager first'
                  : undefined
              }
              size='sm'
              variant='default'
              className='flex items-center gap-1.5'
            >
              {signingLoading ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                  Signing...
                </>
              ) : (
                <>
                  <Send className='h-4 w-4' />
                  Sign & Submit
                </>
              )}
            </Button>
          )}

          {/* Sign & Complete */}
          {canApprove && (
            <Button
              onClick={handleApprove}
              disabled={
                approvingLoading || attestationsLoading || !orderAttestation
              }
              size='sm'
              variant='default'
              className='flex items-center gap-1.5'
            >
              {approvingLoading ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                  Completing...
                </>
              ) : (
                <>
                  <ShieldCheck className='h-4 w-4' />
                  Sign & Complete
                </>
              )}
            </Button>
          )}

          {/* Status Badge */}
          <Badge
            variant={getStatusBadgeVariant(visit.status)}
            className='text-sm'
          >
            {STATUS_DISPLAY_LABELS[visit.status] ?? visit.status}
          </Badge>

          {/* Signatures Badge */}
          {signatures.length > 0 && (
            <Badge variant='default' className='flex items-center gap-1.5'>
              <Printer className='h-3.5 w-3.5' />
              Signed by {signatures.length}{' '}
              {signatures.length === 1 ? 'person' : 'people'}
            </Badge>
          )}
        </div>
      </div>

      {/* Physician & Case Manager */}
      <div className='flex items-center gap-4 px-5 py-2 border-b bg-muted/10'>
        <div className='flex items-center gap-2'>
          <Label className='text-xs text-muted-foreground whitespace-nowrap'>
            Physician
          </Label>
          {isReadonly ? (
            <span className='text-sm'>
              {visit.episode_physician_name ?? 'Not assigned'}
            </span>
          ) : (
            <Select
              value={visit.episode_physician_id ?? undefined}
              onValueChange={v =>
                handleAssignmentChange('assigned_physician_id', v)
              }
            >
              <SelectTrigger selectSize='sm' className='w-48'>
                <SelectValue placeholder='Select physician' />
              </SelectTrigger>
              <SelectContent>
                {physicians.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {formatClinicianName(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <Label className='text-xs text-muted-foreground whitespace-nowrap'>
            Case Manager
          </Label>
          {isReadonly ? (
            <span className='text-sm'>
              {visit.episode_case_manager_name ?? 'Not assigned'}
            </span>
          ) : (
            <Select
              value={visit.episode_case_manager_id ?? undefined}
              onValueChange={v => handleAssignmentChange('case_manager_id', v)}
            >
              <SelectTrigger selectSize='sm' className='w-48'>
                <SelectValue placeholder='Select case manager' />
              </SelectTrigger>
              <SelectContent>
                {caseManagers.map(cm => (
                  <SelectItem key={cm.id} value={cm.id}>
                    {formatClinicianName(cm)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Body */}
      <div className='flex-1 overflow-auto p-4'>
        {isReadonly ? (
          visit.order_type === 'CMS_485' ? (
            <div className='space-y-4'>
              <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
                {cms485PdfLoading ? (
                  <div className='h-full w-full flex items-center justify-center border rounded-md'>
                    <Loader2 className='h-8 w-8 animate-spin' />
                  </div>
                ) : cms485PdfUrl ? (
                  <div className='h-full w-full border rounded-md overflow-hidden bg-muted/10'>
                    <iframe
                      src={cms485PdfUrl}
                      title='CMS 485 Preview'
                      className='h-full w-full'
                    />
                  </div>
                ) : (
                  <div className='h-full w-full flex items-center justify-center border rounded-md text-muted-foreground'>
                    <p>Failed to generate CMS-485 preview.</p>
                  </div>
                )}
              </div>
              {canEditNotes ? (
                <Card>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Comments
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={async value => {
                        await updateTaskNotes({
                          path: { task_id: visit.id },
                          body: { notes: value.trim() || null },
                          client,
                        });
                      }}
                      placeholder='Add comments for this order...'
                    />
                  </CardContent>
                </Card>
              ) : visit.notes ? (
                <Card>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Comments
                    </span>
                    <div className='whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                      {visit.notes}
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          ) : (
            <Card>
              <CardContent className='p-4 space-y-4'>
                {(() => {
                  const data = visit.order_data as Record<
                    string,
                    string
                  > | null;
                  const orderDate = visit.order_date || data?.order_date;
                  const orderTime = visit.order_time || data?.order_time;
                  const orderText = visit.order_text || data?.order_text;
                  const orderSummary =
                    visit.order_summary || data?.order_summary;
                  return (
                    <>
                      <div className='grid grid-cols-2 gap-4 text-sm'>
                        <div>
                          <span className='font-medium text-muted-foreground'>
                            Order Date
                          </span>
                          <p>{orderDate}</p>
                        </div>
                        {orderTime && (
                          <div>
                            <span className='font-medium text-muted-foreground'>
                              Order Time
                            </span>
                            <p>{orderTime}</p>
                          </div>
                        )}
                        {visit.episode_case_manager_name && (
                          <div>
                            <span className='font-medium text-muted-foreground'>
                              Case Manager
                            </span>
                            <p>{visit.episode_case_manager_name}</p>
                          </div>
                        )}
                      </div>
                      {orderText && (
                        <div>
                          <span className='text-sm font-medium text-muted-foreground'>
                            Order Text
                          </span>
                          <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                            {orderText}
                          </div>
                        </div>
                      )}
                      {orderSummary && (
                        <div>
                          <span className='text-sm font-medium text-muted-foreground'>
                            Order Summary
                          </span>
                          <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                            {orderSummary}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                {canEditNotes ? (
                  <div>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Comments
                    </span>
                    <AutoSaveComments
                      className='mt-1'
                      value={visit.notes ?? ''}
                      onSave={async value => {
                        await updateTaskNotes({
                          path: { task_id: visit.id },
                          body: { notes: value.trim() || null },
                          client,
                        });
                      }}
                      placeholder='Add comments for this order...'
                    />
                  </div>
                ) : visit.notes ? (
                  <div>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Comments
                    </span>
                    <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                      {visit.notes}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )
        ) : (
          <>
            {visit.order_type === 'CMS_485' ? (
              <>
                <CMS485OrderForm visit={visit} onSave={() => fetchData()} />
                <Card className='mt-4'>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Comments
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={async value => {
                        await updateTaskNotes({
                          path: { task_id: visit.id },
                          body: { notes: value.trim() || null },
                          client,
                        });
                      }}
                      placeholder='Add comments for this order...'
                    />
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <GenericOrderForm
                  ref={genericFormRef}
                  visit={visit}
                  onSave={() => fetchData()}
                />
                {canEditNotes && (
                  <Card className='mt-4'>
                    <CardContent className='space-y-2'>
                      <span className='text-sm font-medium text-muted-foreground'>
                        Comments
                      </span>
                      <AutoSaveComments
                        value={visit.notes ?? ''}
                        onSave={async value => {
                          await updateTaskNotes({
                            path: { task_id: visit.id },
                            body: { notes: value.trim() || null },
                            client,
                          });
                        }}
                        placeholder='Add comments for this order...'
                      />
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}
        <div className='mt-4'>
          <TaskDocumentsSection taskId={visit.id} />
        </div>

        {signatures.length > 0 ? (
          <div className='mt-4 space-y-2'>
            <h3 className='text-sm font-semibold text-foreground'>
              Signatures
            </h3>
            {signatures.map(sig => (
              <SignatureCard key={sig.id} signature={sig} />
            ))}
          </div>
        ) : null}
      </div>

      <AttestationDialog
        open={signAttestationOpen}
        attestation={orderAttestation}
        signerName={user?.name ?? null}
        isLoading={signingLoading}
        actionLabel='Sign & Submit'
        onConfirm={doSignAndSubmit}
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        attestation={orderAttestation}
        signerName={user?.name ?? null}
        isLoading={approvingLoading}
        actionLabel='Approve & Complete'
        onConfirm={doApprove}
        onCancel={() => setApproveAttestationOpen(false)}
      />
    </div>
  );
};
